import React from 'react';
import {
  Burrito,
  BurritoProps,
  Grid,
  GridItem,
  GridItemAnimated,
} from '../../helpers';
import { Link, ModuleHeader } from '../../primitives';
import styled from '@emotion/styled';
import Spacer from '../../helpers/Spacer';
import CardGridItem from './CardGridItem';
import { IconInterface } from '~/types';
import { mixins } from '../../core/styles/theme/default';
import { rem } from 'polished';

const StyledLink = styled(Link)`
  padding: ${rem(10)} 0;
  margin-bottom: ${rem(-10)};

  .m-richtext & {
    text-decoration: none;
  }
`;

const StyledGridItemAnimated = styled(GridItemAnimated)`
  ${mixins.responsiveStyles({
    'margin-bottom': {
      zero: `${rem(16)}`,
      medium: `${rem(24)}`,
      ultra: `${rem(32)}`,
    },
  })}
`;

const CardGrid = (cardGridProps: CardGridProps) => {
  if (!cardGridProps) return null;

  const {
    title,
    titleIcon,
    titleColor,
    items,
    moduleName,
    background,
    backgroundColor,
    lead,
    moreLink,
    hideMoreLink = false,
    gridItemSize = { zero: 12, ultra: 8 },
    gridItemPush = { zero: 0, ultra: 2 },
    innerGridSize = { zero: 12, large: 8 },
    innerGridItemSize,
    gridItemSizeLarge,
    href,
    moreLinkFallback,
    legend,
    burritoProps,
  } = cardGridProps;

  return (
    <div className={`m-cardgrid ${moduleName}`}>
      <Grid columns={{ zero: 'full', large: 12 }}>
        <GridItem size={gridItemSize} push={gridItemPush}>
          <Burrito
            hasBackground={!!background}
            backgroundColor={backgroundColor}
            spacingCurve={background ? 'peta' : undefined}
            {...(burritoProps ? { ...burritoProps } : {})}
          >
            <ModuleHeader
              title={title}
              lead={lead}
              titleIcon={titleIcon}
              color={titleColor}
            />
            {moduleName === 'Fact' && <Spacer space="deci" />}
            <Grid columns={innerGridSize}>
              {items.filter(Boolean).map((item) => {
                return (
                  <StyledGridItemAnimated
                    size={
                      innerGridItemSize ?? {
                        zero: 'full',
                        small: 12,
                        medium: 6,
                        large: gridItemSizeLarge ?? 4,
                      }
                    }
                    push={{ zero: 0, large: 0 }}
                    key={item.contentful_id}
                    cascade
                    damping={2.5}
                    duration={700}
                    triggerOnce
                  >
                    <CardGridItem name={moduleName} data={item} />
                  </StyledGridItemAnimated>
                );
              })}
            </Grid>
            {legend}
            {!hideMoreLink && href ? (
              <>
                {moreLink ? (
                  <StyledLink
                    linkText={moreLink.linkText}
                    link={{ href }}
                    variant="internal"
                  />
                ) : null}
                {!moreLink && moreLinkFallback ? (
                  <StyledLink
                    linkText={moreLinkFallback}
                    link={{ href }}
                    variant="internal"
                  />
                ) : null}
              </>
            ) : null}
          </Burrito>
        </GridItem>
      </Grid>
    </div>
  );
};

export type CardGridProps = {
  items: any[];
  title?: string;
  titleIcon?: IconInterface;
  titleColor?: 'light';
  moduleName: string;
  background?: boolean;
  backgroundColor?: string;
  lead?: string;
  moreLink?: any;
  hideMoreLink?: boolean;
  gridItemSize?: any;
  gridItemPush?: any;
  innerGridSize?: any;
  innerGridItemSize?: any;
  gridItemSizeLarge?: number;
  href?: string;
  moreLinkFallback?: string;
  legend?: React.ReactNode;
  burritoProps?: BurritoProps;
} | null;

export default CardGrid;
